import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  Breadcrumbs,
  Link as MLink,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Autocomplete,
  Snackbar,
  Alert,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Collapse,
} from '@mui/material';
import { caseService, Case } from '../services/caseService';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { projectAPI, caseAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import CaseEstimation from '../components/CaseEstimation';
import ClientEstimation from '../components/ClientEstimation';
import VendorReassignDialog from '../components/VendorReassignDialog';
import VendorAssignmentHistory from '../components/VendorAssignmentHistory';
import CaseFilesPanel from '../components/case-files/CaseFilesPanel';
import { firmService } from '../services/firmService';
import { notificationService } from '../services/notificationService';
import { formatDate, formatDateTime, formatTime, formatUnixTime, formatUnixTimestamp, formatDateLong, unixToNYInputs, nyInputsToUnix } from '../utils/date';
import { linkifyText } from '../utils/linkifyText';
import { getCaseOwningFirmId } from '../utils/caseUtils';

// CSS animations for active timers
const FIELD_REVISION_SOURCE_LABELS: Record<string, string> = {
  update: 'Case update',
  update_and_comment: 'Update with comment',
  reopen: 'Reopen',
  assign_vendor: 'Assign vendor',
  vendor_assign_creation: 'Vendor assign (on create)',
  reassign_vendor: 'Reassign vendor',
  upload_mrr_delivery: 'Upload (MRR delivery)',
  upload_case_delivery: 'Upload (case delivery)',
  vendor_mrr_sync: 'Vendor MRR sync to client',
  auto_close: 'Auto-close (scheduled)',
};

const timerStyles = `
  @keyframes pulse {
    0% { opacity: 1; }
    50% { opacity: 0.5; }
    100% { opacity: 1; }
  }
  
  @keyframes blink {
    0% { opacity: 1; }
    50% { opacity: 0.3; }
    100% { opacity: 1; }
  }
`;

const isTimeBasedProject = (settings: any): boolean => {
  if (!settings) return false;
  if (settings.estimate_type === 'hours' || settings.estimate_type === 'both') return true;
  return settings.custom_fields?.billing_type === 'Time Based';
};

const CaseView: React.FC = () => {
  const { firmId, caseId } = useParams<{ firmId: string; caseId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPmpAdmin = () => {
    if (!user?.roles) return false;
    return user.roles.some(role => role.slug === 'pmp_admin');
  };

  const isSiteAdmin = () => {
    if (!user?.roles) return false;
    return user.roles.some(role => role.slug === 'site_admin');
  };

  const isPmpAdminOrSiteAdmin = () => {
    return isPmpAdmin() || isSiteAdmin();
  };

  const isSuperAdmin = isPmpAdminOrSiteAdmin();
  // Helper function to check if user has any client role
  const hasClientRole = () => {
    if (!user?.roles) return false;
    const clientRoleSlugs = ['client_admin', 'client_user', 'client_finance_user'];
    return user.roles.some(role => clientRoleSlugs.includes(role.slug));
  };

  // Helper function to check if user is vendor
  const isVendorUser = () => {
    if (!user?.roles) return false;
    const vendorRoleSlugs = ['vendor_staff', 'vendor_admin'];
    return user.roles.some(role => vendorRoleSlugs.includes(role.slug));
  };

  // Helper function to check if user is client firm user
  const isClientFirmUser = () => {
    if (!user?.roles) return false;
    const clientFirmRoleSlugs = ['firm_admin', 'firm_main', 'firm_staff'];
    return user.roles.some(role => clientFirmRoleSlugs.includes(role.slug));
  };
  const [data, setData] = useState<Case | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [firmName, setFirmName] = useState('');
  const [firm, setFirm] = useState<any>(null);

  // Theme helper function based on firm type
  const getThemeColors = () => {
    if (firm?.type === 'vendor') {
      return {
        primary: '#d32f2f', // Dark Red for vendor
        secondary: '#f57c00',
        gradient: 'linear-gradient(135deg, #f57c00 0%, #d32f2f 100%)', // Orange to Red
        light: '#ff6f00',
        dark: '#b71c1c',
      };
    } else if (firm?.type === 'client') {
      return {
        primary: '#1565c0', // Dark Blue for client
        secondary: '#0277bd',
        gradient: 'linear-gradient(135deg, #0277bd 0%, #1565c0 100%)', // Blue gradient
        light: '#0288d1',
        dark: '#0d47a1',
      };
    } else {
      // Default purple theme
      return {
        primary: '#764ba2',
        secondary: '#667eea',
        gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        light: '#8b6fb8',
        dark: '#5a3a7a',
      };
    }
  };

  const themeColors = getThemeColors();

  // Timer state
  const [entries, setEntries] = useState<any[]>([]);
  const [totalSeconds, setTotalSeconds] = useState<number>(0);
  const [deductionSeconds, setDeductionSeconds] = useState<number>(0);
  const [isRunning, setIsRunning] = useState<boolean>(false);
  const [runningStartedAtMs, setRunningStartedAtMs] = useState<number | null>(null);
  const [tick, setTick] = useState<number>(0);
  const [justStopped, setJustStopped] = useState<boolean>(false);
  const [editingEntryId, setEditingEntryId] = useState<number | null>(null);
  const [editingIsSession, setEditingIsSession] = useState<boolean>(false);
  const [activeTimers, setActiveTimers] = useState<any[]>([]);

  // Comments state
  const [comments, setComments] = useState<any[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [visibleCommentsCount, setVisibleCommentsCount] = useState<number>(8);

  // Description read more state
  const [descriptionExpanded, setDescriptionExpanded] = useState<boolean>(false);

  // Refresh trigger for useEffect
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);

  // Field revision history (PMP / Site admin only; API enforces same)
  const [fieldRevisions, setFieldRevisions] = useState<any[]>([]);
  const [fieldRevisionsLoading, setFieldRevisionsLoading] = useState(false);
  const [fieldRevisionsPage, setFieldRevisionsPage] = useState(0);
  const [fieldRevisionsTotal, setFieldRevisionsTotal] = useState(0);
  const [fieldRevisionsPerPage, setFieldRevisionsPerPage] = useState(25);

  // Delete case state
  const [deleteCaseDialogOpen, setDeleteCaseDialogOpen] = useState<boolean>(false);
  const [deletingCase, setDeletingCase] = useState<boolean>(false);

  // Toast notification state
  const [toast, setToast] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });

  // Form state for update/comment panel
  const [users, setUsers] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [notifyUsers, setNotifyUsers] = useState<number[]>([]);
  const [notifyAll, setNotifyAll] = useState<boolean>(false);
  const [notifySuppress, setNotifySuppress] = useState<boolean>(false);
  const [commentBody, setCommentBody] = useState<string>('');
  const [commentType, setCommentType] = useState<string>('internal');
  const [notificationType, setNotificationType] = useState<string>('case_comment');

  // Case update form fields
  const [selectedProject, setSelectedProject] = useState<any>(null);
  const [projectExplicitlyChanged, setProjectExplicitlyChanged] = useState(false);
  const [selectedAssignedUser, setSelectedAssignedUser] = useState<any>(null);
  const [selectedStatus, setSelectedStatus] = useState<any>(null);
  const [selectedPriority, setSelectedPriority] = useState<any>(null);
  const [selectedType, setSelectedType] = useState<any>(null);

  // Timer entry form fields
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [activity, setActivity] = useState<string>('');
  const [refId, setRefId] = useState<string>('');
  const [disposition, setDisposition] = useState<string>('');
  const [editingDispositionLabel, setEditingDispositionLabel] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [startTime, setStartTime] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [endTime, setEndTime] = useState<string>('');
  const [meta, setMeta] = useState<{
    statuses: Record<string, string>;
    priorities: Record<string, string>;
    types: Record<string, string>;
    activities: any[];
    dispositions: any[];
  }>({ statuses: {}, priorities: {}, types: {}, activities: [], dispositions: [] });
  const dispositionDropdownOptions = useMemo(() => {
    const active = meta.dispositions || [];
    if (!disposition) return active;
    const alreadyListed = active.some((d: any) => String(d.id) === String(disposition));
    if (alreadyListed) return active;
    if (editingDispositionLabel) {
      return [...active, { id: disposition, name: `${editingDispositionLabel} (inactive)` }];
    }
    return active;
  }, [meta.dispositions, disposition, editingDispositionLabel]);
  const [note, setNote] = useState<string>('');
  const [deductionText, setDeductionText] = useState<string>('');
  // Project activities for time-based projects
  const [projectActivities, setProjectActivities] = useState<any[]>([]);
  const [selectedActivityIds, setSelectedActivityIds] = useState<number[]>([]);
  const [projectSettings, setProjectSettings] = useState<any>(null);

  // Vendor reassignment state
  const [reassignDialogOpen, setReassignDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [assignmentHistory, setAssignmentHistory] = useState<any[]>([]);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  
  // File delete confirmation state

  // Accordion expansion state
  const [expandedAccordions, setExpandedAccordions] = useState<{
    checklist: boolean;
    caseRecords: boolean;
    caseEstimation: boolean;
    clientEstimation: boolean;
  }>({
    checklist: false,
    caseRecords: false,
    caseEstimation: false,
    clientEstimation: false,
  });

  // Comment composer progressive disclosure
  const [showTaskFields, setShowTaskFields] = useState(false);
  const [showNotifyOptions, setShowNotifyOptions] = useState(false);
  const [submittingComment, setSubmittingComment] = useState(false);

  // Load firm name for breadcrumbs
  useEffect(() => {
    const loadFirm = async () => {
      if (!firmId) return;
      try {
        const res = await firmService.getFirmById(parseInt(firmId));
        setFirm(res.data);
        setFirmName(res.data.name);
      } catch {}
    };
    loadFirm();
  }, [firmId]);

  // Microsoft connection status check - DISABLED FOR NOW
  // useEffect(() => {
  //   const checkMicrosoftStatus = async () => {
  //     try {
  //       const res = await caseAPI.getMicrosoftStatus();
  //       setMicrosoftConnected(res.data.data?.connected || false);
  //     } catch (err) {
  //       setMicrosoftConnected(false);
  //     }
  //   };
  //   checkMicrosoftStatus();
  //   
  //   // Also check on dialog open
  //   if (sharePointDialogOpen) {
  //     checkMicrosoftStatus();
  //   }
  // }, [sharePointDialogOpen]);

  // Handle Microsoft OAuth callback - DISABLED FOR NOW
  // useEffect(() => {
  //   const params = new URLSearchParams(window.location.search);
  //   const microsoftAuth = params.get('microsoft_auth');
  //   if (microsoftAuth === 'success') {
  //     setMicrosoftConnected(true);
  //     showToast('Microsoft account connected successfully', 'success');
  //     // Clean URL
  //     window.history.replaceState({}, document.title, window.location.pathname);
  //   } else if (microsoftAuth === 'error') {
  //     const error = params.get('error');
  //     showToast(`Microsoft connection failed: ${error || 'Unknown error'}`, 'error');
  //     window.history.replaceState({}, document.title, window.location.pathname);
  //   }
  // }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const id = parseInt(caseId!, 10);
        // Pass firm_id to verify case belongs to firm
        try {
          const res = await caseService.getCaseById(id, firmId ? parseInt(firmId) : undefined);
          if (cancelled) return; setData(res.data);
        const timer = await caseService.listTimeEntries(id);
        const loadedEntries = timer.data.entries || [];
        if (cancelled) return; setEntries(loadedEntries);
        if (cancelled) return; setTotalSeconds(timer.data.total_seconds || 0);
        // Determine running state from API session
        const runningSession = (timer.data as any).running_session;
        if (runningSession && typeof runningSession.started_at === 'number') {
          if (cancelled) return; setIsRunning(true);
          if (cancelled) return; setRunningStartedAtMs(runningSession.started_at * 1000);
          if (cancelled) return; setJustStopped(false);
        } else {
          if (cancelled) return; setIsRunning(false);
        }
        // Prefer pending session from API, else fall back to non-finalized entry
        const pendingSession = (timer.data as any).pending_session;
        const pending = pendingSession || ((user
          ? loadedEntries.find((e: any) => e.user?.id === user.id && e.stopped_at && e.is_finalized === false)
          : null) || loadedEntries.find((e: any) => e.stopped_at && e.is_finalized === false));
        if (pending) {
          if (cancelled) return; setEditingEntryId(pending.session_id || pending.id);
          if (cancelled) return; setEditingIsSession(!!pending.session_id);
          if (cancelled) return; setSelectedUserId((pending.user?.id) || selectedUserId || (user?.id ?? null));
          const sUnix = typeof pending.started_at === 'number' ? pending.started_at : undefined;
          const eUnix = typeof pending.stopped_at === 'number' ? pending.stopped_at : undefined;
          const sNY = sUnix ? unixToNYInputs(sUnix) : null;
          const eNY = eUnix ? unixToNYInputs(eUnix) : null;
          if (cancelled) return; setStartDate(sNY ? sNY.date : '');
          if (cancelled) return; setStartTime(sNY ? sNY.time : '');
          if (cancelled) return; setEndDate(eNY ? eNY.date : '');
          if (cancelled) return; setEndTime(eNY ? eNY.time : '');
          // clear optional fields; they will be provided on Save
          if (cancelled) return; setActivity('');
          if (cancelled) return; setRefId('');
          if (cancelled) return; setDisposition('');
          if (cancelled) return; setEditingDispositionLabel('');
          if (cancelled) return; setNote('');
          if (cancelled) return; setDeductionText('');
          if (cancelled) return; setSelectedActivityIds([]);
          if (cancelled) return; setJustStopped(true);
        }
        try {
          const usersRes = await caseAPI.getCommentNotifyUsers(id);
          if (cancelled) return; setUsers(Array.isArray(usersRes.data.data) ? usersRes.data.data : []);
        } catch (_e) {
          if (cancelled) return; setUsers([]);
        }
        
        // Load projects for this case's owning firm only
        try {
          const firmIdToUse = getCaseOwningFirmId(res?.data);
          let projectsData: any[] = [];
          if (firmIdToUse) {
            const projectsRes = await projectAPI.getAll({ firm_id: firmIdToUse });
            projectsData = (projectsRes?.data?.data?.data) || (projectsRes?.data?.data) || [];
            if (!Array.isArray(projectsData)) projectsData = [];
          }
          if (cancelled) return; setProjects(projectsData);
        } catch (_e) {
          if (cancelled) return; setProjects([]);
        }
        
        try {
          const metaRes = await caseService.getMeta({ case_id: id });
          if (cancelled) return; setMeta((metaRes && (metaRes as any).data) || { statuses: {}, priorities: {}, types: {}, activities: [], dispositions: [] });
        } catch (_e) {
          if (cancelled) return; setMeta({ statuses: {}, priorities: {}, types: {}, activities: [], dispositions: [] });
        }
        
        // Load project settings and activities if project is time-based
        if (res?.data?.project_id) {
          try {
            const settingsRes = await projectAPI.getSettings(res.data.project_id);
            const settings = settingsRes.data.data;
            if (cancelled) return; setProjectSettings(settings);
            
            // Check if project is time-based
            if (settings && isTimeBasedProject(settings)) {
              const activitiesRes = await projectAPI.getActivities(res.data.project_id);
              if (cancelled) return; setProjectActivities((activitiesRes.data.data || []).filter((a: any) => a.is_active && a.is_billable));
            } else {
              if (cancelled) return; setProjectActivities([]);
            }
          } catch (_e) {
            if (cancelled) return; setProjectActivities([]);
            if (cancelled) return; setProjectSettings(null);
          }
        }
        try {
          const activeTimersRes = await caseService.getActiveTimers(id);
          if (cancelled) return; setActiveTimers(activeTimersRes.data.active_timers || []);
        } catch (_e) {
          if (cancelled) return; setActiveTimers([]);
        }
        try {
          const commentsRes = await caseService.getComments(id);
          if (cancelled) return; setComments(Array.isArray(commentsRes.data) ? commentsRes.data : []);
        } catch (_e) {
          if (cancelled) return; setComments([]);
        }
        } catch (error: any) {
          // Handle case access errors
          if (error.message && error.message.includes('does not belong to the specified firm')) {
            showToast('This case does not belong to the selected firm. Redirecting to case list...', 'error');
            setTimeout(() => {
              if (firmId) {
                navigate(`/firms/${firmId}/cases`);
              } else {
                navigate('/cases');
              }
            }, 2000);
            return;
          }
          // Re-throw other errors
          throw error;
        }
      } catch (error: any) {
        // Handle any other errors during load
        showToast(error.message || 'Failed to load case', 'error');
        if (firmId) {
          navigate(`/firms/${firmId}/cases`);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [caseId, user?.id, refreshTrigger]);

  // Mark notification as read when comment is viewed via URL hash
  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.startsWith('#comment-')) {
      const commentId = parseInt(hash.replace('#comment-', ''), 10);
      if (commentId && !isNaN(commentId)) {
        // Wait a bit for the comment to render, then mark as read
        const timer = setTimeout(() => {
          notificationService.markAsReadByComment(commentId).catch(err => {
            console.error('Failed to mark notification as read:', err);
          });
        }, 500);
        return () => clearTimeout(timer);
      }
    }
  }, [comments]);

  // Mark notification as read when comment scrolls into view
  useEffect(() => {
    if (comments.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const elementId = entry.target.id;
            if (elementId && elementId.startsWith('comment-')) {
              const commentId = parseInt(elementId.replace('comment-', ''), 10);
              if (commentId && !isNaN(commentId)) {
                notificationService.markAsReadByComment(commentId).catch(err => {
                  console.error('Failed to mark notification as read:', err);
                });
              }
            }
          }
        });
      },
      { threshold: 0.5 } // Mark as read when 50% visible
    );

    // Observe all comment elements
    comments.forEach((comment) => {
      const element = document.getElementById(`comment-${comment.id}`);
      if (element) {
        observer.observe(element);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, [comments]);

  // Live ticking when running
  useEffect(() => {
    if (!isRunning) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [isRunning]);

  // Handle "Send notification to all users" checkbox
  useEffect(() => {
    if (notifyAll && users.length > 0) {
      setNotifyUsers(users.map(user => user.id));
    } else if (!notifyAll) {
      setNotifyUsers([]);
    }
  }, [notifyAll, users]);

  // Refresh active timers every second when there are active timers
  useEffect(() => {
    if (activeTimers.length === 0) return;
    const id = setInterval(async () => {
      if (!data) return;
      try {
        const activeTimersRes = await caseService.getActiveTimers(data.id);
        setActiveTimers(activeTimersRes.data.active_timers || []);
      } catch (_e) {
        // Silently fail, don't update state
      }
    }, 1000);
    return () => clearInterval(id);
  }, [activeTimers.length, data]);

  useEffect(() => {
    setFieldRevisions([]);
    setFieldRevisionsPage(0);
    setFieldRevisionsTotal(0);
  }, [data?.id]);

  const refreshActiveTimers = async () => {
    if (!data) return;
    try {
      const activeTimersRes = await caseService.getActiveTimers(data.id);
      setActiveTimers(activeTimersRes.data.active_timers || []);
    } catch (_e) {
      setActiveTimers([]);
    }
  };

  const showToast = (message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    setToast({
      open: true,
      message,
      severity
    });
  };

  const loadFieldRevisions = async (pageIndex: number, perPage: number = fieldRevisionsPerPage) => {
    if (!data?.id) return;
    setFieldRevisionsLoading(true);
    try {
      const res = await caseService.listFieldRevisions(data.id, {
        per_page: perPage,
        page: pageIndex + 1,
      });
      const p = res.data;
      setFieldRevisions(p.data ?? []);
      setFieldRevisionsTotal(p.total ?? 0);
      setFieldRevisionsPage(pageIndex);
    } catch (error: any) {
      showToast(error.message || 'Failed to load field revision history', 'error');
    } finally {
      setFieldRevisionsLoading(false);
    }
  };

  const handleDeleteCase = async () => {
    setDeletingCase(true);
    try {
      await caseService.deleteCase(Number(caseId));
      setDeleteCaseDialogOpen(false);
      showToast('Case deleted successfully', 'success');
      setTimeout(() => navigate(`/firms/${firmId}/cases`), 1000);
    } catch (error: any) {
      showToast(error.message || 'Failed to delete case', 'error');
    } finally {
      setDeletingCase(false);
    }
  };

  const handleReassignVendor = async (reassignData: any) => {
    try {
      await caseService.reassignVendor(Number(caseId), reassignData);
      showToast('Vendor reassigned successfully', 'success');
      setRefreshTrigger(prev => prev + 1); // Trigger data reload
    } catch (error: any) {
      throw error; // Re-throw to be caught by dialog
    }
  };

  const loadAssignmentHistory = async () => {
    try {
      const response = await caseService.getVendorAssignmentHistory(Number(caseId));
      setAssignmentHistory(response.data || []);
      setHistoryDialogOpen(true);
    } catch (error: any) {
      showToast(error.message || 'Failed to load assignment history', 'error');
    }
  };

  const handleAssignVendor = async (assignData: any) => {
    try {
      await caseService.assignVendor(Number(caseId), assignData);
      showToast('Vendor assigned successfully', 'success');
      setRefreshTrigger(prev => prev + 1); // Trigger data reload
    } catch (error: any) {
      throw error; // Re-throw to be caught by dialog
    }
  };

  const handleCloseToast = () => {
    setToast(prev => ({ ...prev, open: false }));
  };

  // Inject CSS animations
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = timerStyles;
    document.head.appendChild(styleElement);
    
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  // Reset visible comments when list changes
  useEffect(() => {
    setVisibleCommentsCount((prev) => Math.min(5, comments.length || 0));
  }, [comments.length]);

  if (loading) {
    return <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>;
  }

  if (!data) {
    return <Typography variant="body2" color="text.secondary">Case not found.</Typography>;
  }

  const totalHms = (() => {
    const runningNow = isRunning && runningStartedAtMs ? Math.max(0, Math.floor((Date.now() - runningStartedAtMs) / 1000)) : 0;
    const s = totalSeconds + runningNow;
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return `${h} hrs, ${m} mins, ${sec} secs`;
  })();

  const liveRunHms = (() => {
    if (!isRunning || !runningStartedAtMs) return '';
    const s = Math.max(0, Math.floor((Date.now() - runningStartedAtMs) / 1000));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return `${h} hrs, ${m} mins, ${sec} secs`;
  })();

  const handleStartTimer = async () => {
    if (!data) return;
    const id = data.id;
    const payload: any = {};
    if (isSuperAdmin && startDate && startTime) {
      payload.started_at = Math.floor(new Date(`${startDate}T${startTime}`).getTime() / 1000);
    }
    try {
      await caseService.startTimer(id, Object.keys(payload).length ? payload : undefined);
      const timer = await caseService.listTimeEntries(id);
      const loadedEntries = timer.data.entries || [];
      setEntries(loadedEntries);
      setTotalSeconds(timer.data.total_seconds || 0);
      const runningSession = (timer.data as any).running_session;
      if (runningSession && typeof runningSession.started_at === 'number') {
        setIsRunning(true);
        setRunningStartedAtMs(runningSession.started_at * 1000);
      } else {
        setIsRunning(false);
        setRunningStartedAtMs(null);
      }
      setJustStopped(false);
      // Refresh active timers
      await refreshActiveTimers();
    } catch (e: any) {
      showToast(e.message || 'Failed to start timer', 'error');
    }
  };

  const handleStopTimer = async () => {
    if (!data) return;
    const id = data.id;
    try {
      const stopResponse = await caseService.stopTimer(id);
      
      // Add a small delay to ensure backend has processed the stop request
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const timer = await caseService.listTimeEntries(id);
      const loadedEntries = timer.data.entries || [];
      setEntries(loadedEntries);
      setTotalSeconds(timer.data.total_seconds || 0);
      setIsRunning(false);
      setRunningStartedAtMs(null);
      setJustStopped(true);
      // populate from pending_session
      const pendingSession = (timer.data as any).pending_session;
      if (pendingSession) {
        setEditingEntryId(pendingSession.session_id);
        setEditingIsSession(true);
        setSelectedUserId(user?.id || null);
        const startedUnix = pendingSession.started_at as number | undefined;
        const stoppedUnix = pendingSession.stopped_at as number | undefined;

        // Race-condition guard: if both timestamps are identical use the stopTimer response
        const hasSameTimestamps = startedUnix && stoppedUnix && startedUnix === stoppedUnix;
        const resolvedStartUnix = (hasSameTimestamps && stopResponse?.data?.started_at) ? stopResponse.data.started_at : startedUnix;
        const resolvedStopUnix  = (hasSameTimestamps && stopResponse?.data?.stopped_at)  ? stopResponse.data.stopped_at
                                : (stoppedUnix || (startedUnix ? Math.floor(Date.now() / 1000) : undefined));

        const sNY = resolvedStartUnix ? unixToNYInputs(resolvedStartUnix) : null;
        const eNY = resolvedStopUnix  ? unixToNYInputs(resolvedStopUnix)  : null;
        setStartDate(sNY ? sNY.date : '');
        setStartTime(sNY ? sNY.time : '');
        setEndDate(eNY ? eNY.date : '');
        setEndTime(eNY ? eNY.time : '');
        setDeductionText('');
        setNote('');
        setActivity('');
        setRefId('');
        setDisposition('');
        setEditingDispositionLabel('');
      }
      // Refresh active timers
      await refreshActiveTimers();
    } catch (e: any) {
      showToast(e.message || 'Failed to stop timer', 'error');
    }
  };

  const handleResumeTimer = async () => {
    if (!data) return;
    try {
      await caseService.resumeTimer(data.id);
      const timer = await caseService.listTimeEntries(data.id);
      const runningSession = (timer.data as any).running_session;
      if (runningSession && typeof runningSession.started_at === 'number') {
        setIsRunning(true);
        setRunningStartedAtMs(runningSession.started_at * 1000);
        setJustStopped(false);
      }
      // Refresh active timers
      await refreshActiveTimers();
    } catch (e: any) {
      showToast(e.message || 'Failed to resume timer', 'error');
    }
  };

  const handleResetTimer = () => {
    (async () => {
      if (!data) return;
      try {
        await caseService.resetTimer(data.id, { session_id: editingEntryId });
        // clear local form/state
        setEditingEntryId(null);
        setStartDate('');
        setStartTime('');
        setEndDate('');
        setEndTime('');
        setActivity('');
        setDisposition('');
        setEditingDispositionLabel('');
        setRefId('');
        setNote('');
        setDeductionText('');
        setSelectedActivityIds([]);
        setJustStopped(false);
        // reload timer state
        const timer = await caseService.listTimeEntries(data.id);
        setEntries(timer.data.entries || []);
        setTotalSeconds(timer.data.total_seconds || 0);
        const runningSession = (timer.data as any).running_session;
        if (runningSession && typeof runningSession.started_at === 'number') {
          setIsRunning(true);
          setRunningStartedAtMs(runningSession.started_at * 1000);
        } else {
          setIsRunning(false);
          setRunningStartedAtMs(null);
        }
      } catch (e: any) {
        showToast(e.message || 'Failed to reset timer', 'error');
      }
    })();
  };

  return (
    <Box>
      {firmId && (
        <Breadcrumbs sx={{ mb: 2 }}>
          <MLink
            color="inherit"
            href="#"
            onClick={(e) => { e.preventDefault(); navigate('/firms'); }}
            underline="hover"
          >
            Firms
          </MLink>
          <MLink
            color="inherit"
            href="#"
            onClick={(e) => { e.preventDefault(); navigate(`/firms/${firmId}`); }}
            underline="hover"
          >
            {firmName || `Firm #${firmId}`}
          </MLink>
          <MLink
            color="inherit"
            href="#"
            onClick={(e) => { e.preventDefault(); navigate(`/firms/${firmId}/cases`); }}
            underline="hover"
          >
            Cases
          </MLink>
          <Typography color="text.primary">Case #{data.id}</Typography>
        </Breadcrumbs>
      )}

      <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
        <Button variant="outlined" onClick={() => navigate(-1)}>
          Back
        </Button>
        <Typography variant="h4" sx={{ flex: 1 }}>{data.title}</Typography>
        <Button
          variant="outlined"
          onClick={() => navigate(`/firms/${firmId}/cases/${caseId}/edit`)}
        >
          Edit Case
        </Button>
        {isPmpAdminOrSiteAdmin() && (
          <Button
            variant="outlined"
            color="error"
            onClick={() => setDeleteCaseDialogOpen(true)}
          >
            Delete Case
          </Button>
        )}
      </Box>
      
      {/* 50/50: Case Information | Comments */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
          gap: 2,
          alignItems: 'stretch',
          mb: 2,
        }}
      >
        {/* Left: Case Information + Settings */}
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          {/* 📋 Case Information */}
          <Paper sx={{ 
            p: 3, 
            mb: 2, 
            flex: 1,
            background: themeColors.gradient,
            color: 'white',
            borderRadius: 2,
            boxShadow: '0 4px 16px rgba(0,0,0,0.2)'
          }}>
            <Typography variant="h5" gutterBottom sx={{ 
              fontWeight: 'bold',
              textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
              mb: 2
            }}>
              📋 Case Information
            </Typography>
            
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, 
              gap: 2 
            }}>
              <Box>
                <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.8)', mb: 0.5 }}>
                  Project:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 2 }}>
                  {data.project?.name || 'N/A'}
                </Typography>
                <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.8)', mb: 0.5 }}>
                  Status:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 2 }}>
                  {data.status ? data.status.replace('_', ' ').toUpperCase() : 'N/A'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.8)', mb: 0.5 }}>
                  Priority:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 2 }}>
                  {data.priority ? data.priority.replace('_', ' ').toUpperCase() : 'Normal'}
                </Typography>
                <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.8)', mb: 0.5 }}>
                  Type:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 2 }}>
                  {data.type ? data.type.replace('_', ' ').toUpperCase() : 'N/A'}
                </Typography>
                <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.8)', mb: 0.5 }}>
                  Due Date:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 2, color: (() => {
                  if (!(data as any).due_date) return 'inherit';
                  const activeStatuses = ['open', 'wip', 'pending', 'awaiting_info', 'quality_check'];
                  const isActive = activeStatuses.includes(String((data as any).status || '').toLowerCase());
                  const isOverdue = isActive && new Date((data as any).due_date) < new Date(new Date().toDateString());
                  return isOverdue ? '#ffcdd2' : 'inherit';
                })() }}>
                  {(data as any).due_date
                    ? new Date((data as any).due_date).toLocaleDateString()
                    : '—'}
                  {(data as any).due_date && (() => {
                    const activeStatuses = ['open', 'wip', 'pending', 'awaiting_info', 'quality_check'];
                    const isActive = activeStatuses.includes(String((data as any).status || '').toLowerCase());
                    const isOverdue = isActive && new Date((data as any).due_date) < new Date(new Date().toDateString());
                    return isOverdue ? ' (OVERDUE)' : '';
                  })()}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, 
              gap: 2,
              mt: 1
            }}>
              <Box>
                <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.8)', mb: 0.5 }}>
                  Assigned to:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 2 }}>
                  {(data as any).assigned_to?.name || '—'}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ mt: 2, pt: 2 }}>
              <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.8)', mb: 0.5 }}>
                Description:
              </Typography>
              <Box>
                {(() => {
                  const description = (data as any).description || '—';
                  const maxLength = 500;
                  const shouldTruncate = description.length > maxLength && !descriptionExpanded;
                  const displayText = shouldTruncate 
                    ? description.substring(0, maxLength) + '...' 
                    : description;
                  
                  return (
                    <>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          fontWeight: 'bold', 
                          mb: shouldTruncate || descriptionExpanded ? 1 : 2,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          overflowWrap: 'anywhere',
                          '& a': {
                            color: 'rgba(255,255,255,0.95)',
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        {linkifyText(displayText)}
                      </Typography>
                      {description.length > maxLength && (
                        <Button
                          onClick={() => setDescriptionExpanded(!descriptionExpanded)}
                          sx={{
                            color: 'rgba(255,255,255,0.9)',
                            textTransform: 'none',
                            fontSize: '0.875rem',
                            minWidth: 'auto',
                            p: 0,
                            '&:hover': {
                              backgroundColor: 'transparent',
                              textDecoration: 'underline',
                            }
                          }}
                        >
                          {descriptionExpanded ? 'Read less' : 'Read more'}
                        </Button>
                      )}
                    </>
                  );
                })()}
              </Box>
            </Box>
            
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
              <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.8)', mb: 0.5 }}>
                Submitted by:
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {data.creator?.name || 'Unknown'} on {formatDateLong(data.created_at)} - {formatTime(data.created_at, { hour: 'numeric', minute: '2-digit', hour12: true })}
              </Typography>
            </Box>

            {/* Vendor Assignment Info - Only for PMP Admin on client cases */}
            {isSuperAdmin && (data as any)?.client_assignment && (data as any).client_assignment.vendor_case_id && (
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
                <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.8)', mb: 0.5 }}>
                  Assigned to Vendor:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {(data as any).client_assignment.vendorFirm?.name || (data as any).client_assignment.vendor_firm?.name || 'Unknown Vendor'}
                </Typography>
                {((data as any).client_assignment.vendorUser || (data as any).client_assignment.vendor_user) && (
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', mb: 1 }}>
                    Vendor User: {(data as any).client_assignment.vendorUser?.name || (data as any).client_assignment.vendor_user?.name || '—'}
                  </Typography>
                )}
                {((data as any).client_assignment.vendorCase || (data as any).client_assignment.vendor_case_id) && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      const vendorFirmId = (data as any).client_assignment.vendorFirm?.id || (data as any).client_assignment.vendor_firm_id;
                      const vendorCaseId = (data as any).client_assignment.vendorCase?.id || (data as any).client_assignment.vendor_case_id;
                      if (vendorFirmId && vendorCaseId) {
                        navigate(`/firms/${vendorFirmId}/cases/${vendorCaseId}`);
                      }
                    }}
                    sx={{
                      color: 'rgba(255,255,255,0.9)',
                      borderColor: 'rgba(255,255,255,0.5)',
                      '&:hover': {
                        borderColor: 'rgba(255,255,255,0.8)',
                        backgroundColor: 'rgba(255,255,255,0.1)'
                      }
                    }}
                  >
                    View Vendor Case
                  </Button>
                )}
              </Box>
            )}

            {/* Vendor Assignment Section - Only show for vendor cases (not client cases) */}
            {(data as any)?.vendor_assignment && (
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
                <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.8)', mb: 0.5 }}>
                  Assigned Vendor:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {(data as any)?.assignedVendor?.name || '—'}
                </Typography>
                {(data as any)?.assignedVendorUser && (
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', mb: 1 }}>
                    User: {(data as any)?.assignedVendorUser?.name}
                  </Typography>
                )}
                
                {/* View Client Case button - Only for Site Admin or PMP Admin */}
                {isPmpAdminOrSiteAdmin() && 
                 ((data as any)?.vendor_assignment?.clientCase || (data as any)?.vendor_assignment?.client_case_id) && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      const vendorAssignment = (data as any)?.vendor_assignment;
                      const clientCase = vendorAssignment?.clientCase;
                      const clientCaseId = clientCase?.id || vendorAssignment?.client_case_id;
                      // Try multiple ways to get the client firm ID
                      const clientFirmId = clientCase?.project?.firm_id || 
                                          vendorAssignment?.clientFirm?.id || 
                                          vendorAssignment?.client_firm_id;
                      if (clientFirmId && clientCaseId) {
                        navigate(`/firms/${clientFirmId}/cases/${clientCaseId}`);
                      }
                    }}
                    sx={{
                      mt: 1,
                      color: 'rgba(255,255,255,0.9)',
                      borderColor: 'rgba(255,255,255,0.5)',
                      '&:hover': {
                        borderColor: 'rgba(255,255,255,0.8)',
                        backgroundColor: 'rgba(255,255,255,0.1)'
                      }
                    }}
                  >
                    View Client Case
                  </Button>
                )}
                
                {/* Only show reassignment buttons to PMP Admin */}
                {isSuperAdmin && (
                  <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setReassignDialogOpen(true)}
                      sx={{
                        color: 'white',
                        borderColor: 'rgba(255,255,255,0.5)',
                        '&:hover': {
                          borderColor: 'white',
                          backgroundColor: 'rgba(255,255,255,0.1)',
                        },
                      }}
                    >
                      Reassign Vendor
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={loadAssignmentHistory}
                      sx={{
                        color: 'white',
                        borderColor: 'rgba(255,255,255,0.5)',
                        '&:hover': {
                          borderColor: 'white',
                          backgroundColor: 'rgba(255,255,255,0.1)',
                        },
                      }}
                    >
                      View History
                    </Button>
                  </Box>
                )}
              </Box>
            )}
          </Paper>

          {/* Case Settings */}
          <Paper sx={{ p: 3, mb: 2 }}>
            <Typography variant="h6" gutterBottom>Case Settings</Typography>
            <Typography variant="body2">Free Case?: {data.free_case ? 'Yes' : 'No'}</Typography>
          </Paper>

          {isPmpAdmin() && (
            <Accordion
              sx={{ mb: 0 }}
              onChange={(_e, expanded) => {
                if (expanded && data?.id) {
                  loadFieldRevisions(0);
                }
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Case Revision History
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1, alignSelf: 'center' }}>
                  (PMP admin only)
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  When case fields change, a row is stored per field: previous value, new value, source, and user.
                </Typography>
                {fieldRevisionsLoading && fieldRevisions.length === 0 ? (
                  <Box display="flex" justifyContent="center" py={2}>
                    <CircularProgress size={28} />
                  </Box>
                ) : fieldRevisions.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No revisions recorded yet.
                  </Typography>
                ) : (
                  <>
                    <Table size="small" sx={{ tableLayout: 'fixed' }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: '18%' }}>When</TableCell>
                          <TableCell sx={{ width: '12%' }}>Field</TableCell>
                          <TableCell sx={{ width: '22%' }}>Old value</TableCell>
                          <TableCell sx={{ width: '22%' }}>New value</TableCell>
                          <TableCell sx={{ width: '16%' }}>Source</TableCell>
                          <TableCell sx={{ width: '10%' }}>User</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {fieldRevisions.map((row) => (
                          <TableRow key={row.id}>
                            <TableCell sx={{ verticalAlign: 'top', wordBreak: 'break-word' }}>
                              {row.created_at
                                ? formatDateTime(new Date(row.created_at))
                                : '—'}
                            </TableCell>
                            <TableCell sx={{ verticalAlign: 'top', wordBreak: 'break-word' }}>
                              {row.field_name}
                            </TableCell>
                            <TableCell
                              sx={{ verticalAlign: 'top', wordBreak: 'break-word' }}
                              title={row.old_value ?? ''}
                            >
                              {row.old_value ?? '—'}
                            </TableCell>
                            <TableCell
                              sx={{ verticalAlign: 'top', wordBreak: 'break-word' }}
                              title={row.new_value ?? ''}
                            >
                              {row.new_value ?? '—'}
                            </TableCell>
                            <TableCell sx={{ verticalAlign: 'top', wordBreak: 'break-word' }}>
                              {FIELD_REVISION_SOURCE_LABELS[row.source] || row.source}
                            </TableCell>
                            <TableCell sx={{ verticalAlign: 'top', wordBreak: 'break-word' }}>
                              {row.user?.name ?? (row.source === 'auto_close' ? 'System' : '—')}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    <TablePagination
                      component="div"
                      count={fieldRevisionsTotal}
                      page={fieldRevisionsPage}
                      onPageChange={(_e, newPage) => loadFieldRevisions(newPage)}
                      rowsPerPage={fieldRevisionsPerPage}
                      onRowsPerPageChange={(e) => {
                        const next = parseInt(e.target.value, 10);
                        setFieldRevisionsPerPage(next);
                        loadFieldRevisions(0, next);
                      }}
                      rowsPerPageOptions={[10, 25, 50]}
                    />
                  </>
                )}
              </AccordionDetails>
            </Accordion>
          )}
        </Box>

        {/* Right: Comments (50%) */}
        <Paper
          sx={{
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            minHeight: { lg: '100%' },
            height: { lg: '100%' },
            overflow: 'hidden',
          }}
        >
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            Comments{comments.length > 0 ? ` (${comments.length})` : ''}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Read the thread below, then add a reply. Expand options only if you need to change task fields or notifications.
          </Typography>

          {/* Comment list first */}
          <Box sx={{ flex: 1, minHeight: 220, overflowY: 'auto', mb: 2 }}>
            {comments.length > 0 ? (
              <Box>
                {comments.slice(0, visibleCommentsCount).map((comment) => (
                  <Box
                    key={comment.id}
                    id={`comment-${comment.id}`}
                    sx={{
                      mb: 1.5,
                      p: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      backgroundColor: comment.type === 'internal' ? 'grey.50' : 'background.paper',
                      scrollMarginTop: '20px',
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', lineHeight: 1.3 }}>
                          {comment.user?.name || comment.user?.username || 'Unknown User'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDateTime(comment.created_at)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.75 }}>
                        {!hasClientRole() && (
                          <Chip
                            label={comment.type === 'internal' ? 'Internal' : 'External'}
                            size="small"
                            color={comment.type === 'internal' ? 'primary' : 'secondary'}
                          />
                        )}
                        <Chip
                          label={comment.notification_type?.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()) || 'Case Comment'}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                    </Box>
                    <Typography
                      variant="body1"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                        '& a': { color: 'primary.main' },
                      }}
                    >
                      {linkifyText(comment.body || '')}
                    </Typography>
                  </Box>
                ))}
                {visibleCommentsCount < comments.length && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1, mb: 1 }}>
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => setVisibleCommentsCount((c) => Math.min(c + 8, comments.length))}
                    >
                      Show older comments ({comments.length - visibleCommentsCount} more)
                    </Button>
                  </Box>
                )}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                No comments yet. Add the first reply below.
              </Typography>
            )}
          </Box>

          {/* Reply composer */}
          <Box
            sx={{
              pt: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
              flexShrink: 0,
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Add a reply
            </Typography>
            <TextField
              multiline
              minRows={3}
              maxRows={8}
              value={commentBody}
              onChange={(e) => setCommentBody(e.target.value)}
              sx={{ width: '100%', mb: 1 }}
              placeholder="Write your comment…"
              size="small"
            />

            {!hasClientRole() && (
              <>
                <Button
                  size="small"
                  onClick={() => setShowTaskFields((v) => !v)}
                  endIcon={
                    <ExpandMoreIcon
                      sx={{
                        transform: showTaskFields ? 'rotate(180deg)' : 'none',
                        transition: 'transform 0.2s',
                      }}
                    />
                  }
                  sx={{ textTransform: 'none', mb: 0.5, mr: 1 }}
                >
                  Also update task fields
                </Button>
              <Collapse in={showTaskFields}>
                <Box sx={{ mb: 1.5, pt: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontStyle: 'italic' }}>
                    Optional — only fill fields you want to change
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, mb: 1.5 }}>
                    <TextField
                      label="Project"
                      select
                      value={selectedProject?.id || ''}
                      onChange={(e) => {
                        const project = projects.find(p => p.id === e.target.value);
                        setSelectedProject(project || null);
                        setProjectExplicitlyChanged(!!project);
                      }}
                      fullWidth
                      size="small"
                    >
                      <MenuItem value="">- No change -</MenuItem>
                      {projects.map((p) => (
                        <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      label="Assigned to"
                      select
                      value={selectedAssignedUser?.id || ''}
                      onChange={(e) => {
                        const u = users.find(usr => usr.id === e.target.value);
                        setSelectedAssignedUser(u || null);
                      }}
                      fullWidth
                      size="small"
                    >
                      <MenuItem value="">- No change -</MenuItem>
                      {users.map((u) => (
                        <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
                      ))}
                    </TextField>
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 1.5 }}>
                    <TextField
                      label="Status"
                      select
                      value={selectedStatus?.value || ''}
                      onChange={(e) => {
                        const status = Object.entries(meta.statuses || {}).find(([k]) => k === e.target.value);
                        setSelectedStatus(status ? { value: status[0], label: status[1] } : null);
                      }}
                      fullWidth
                      size="small"
                    >
                      <MenuItem value="">- No change -</MenuItem>
                      {Object.entries(meta.statuses || {}).map(([k, v]) => (
                        <MenuItem key={k} value={k}>{v}</MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      label="Priority"
                      select
                      value={selectedPriority?.value || ''}
                      onChange={(e) => {
                        const priority = Object.entries(meta.priorities || {}).find(([k]) => k === e.target.value);
                        setSelectedPriority(priority ? { value: priority[0], label: priority[1] } : null);
                      }}
                      fullWidth
                      size="small"
                    >
                      <MenuItem value="">- No change -</MenuItem>
                      {Object.entries(meta.priorities || {}).map(([k, v]) => (
                        <MenuItem key={k} value={k}>{v}</MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      label="Type"
                      select
                      value={selectedType?.value || ''}
                      onChange={(e) => {
                        const type = Object.entries(meta.types || {}).find(([k]) => k === e.target.value);
                        setSelectedType(type ? { value: type[0], label: type[1] } : null);
                      }}
                      fullWidth
                      size="small"
                    >
                      <MenuItem value="">- No change -</MenuItem>
                      {Object.entries(meta.types || {}).map(([k, v]) => (
                        <MenuItem key={k} value={k}>{v}</MenuItem>
                      ))}
                    </TextField>
                  </Box>
                </Box>
              </Collapse>
            </>
          )}

          <Button
            size="small"
            onClick={() => setShowNotifyOptions((v) => !v)}
            endIcon={
              <ExpandMoreIcon
                sx={{
                  transform: showNotifyOptions ? 'rotate(180deg)' : 'none',
                  transition: 'transform 0.2s',
                }}
              />
            }
            sx={{ textTransform: 'none', mb: 1 }}
          >
            Notification options
          </Button>
          <Collapse in={showNotifyOptions}>
            <Box sx={{ mb: 1.5, pt: 0.5 }}>
              {!hasClientRole() && (
                <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
                  <Autocomplete
                    options={[
                      { value: 'internal', label: 'Internal' },
                      { value: 'external', label: 'External' }
                    ]}
                    getOptionLabel={(option) => option.label || ''}
                    value={commentType ? { value: commentType, label: commentType === 'internal' ? 'Internal' : 'External' } : null}
                    onChange={(_e, value) => setCommentType(value?.value || 'internal')}
                    renderInput={(params) => <TextField {...params} label="Comment Type" size="small" />}
                    sx={{ minWidth: 160, flex: 1 }}
                  />
                  <Autocomplete
                    options={[
                      { value: 'case_comment', label: 'Case Comment' },
                      { value: 'response_expected', label: 'Response Expected' },
                      { value: 'approval_expected', label: 'Approval Expected' }
                    ]}
                    getOptionLabel={(option) => option.label || ''}
                    value={notificationType ? { value: notificationType, label: notificationType.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()) } : null}
                    onChange={(_e, value) => setNotificationType(value?.value || 'case_comment')}
                    renderInput={(params) => <TextField {...params} label="Notification Type" size="small" />}
                    sx={{ minWidth: 160, flex: 1 }}
                  />
                </Box>
              )}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={notifyAll}
                    onChange={(e) => {
                      setNotifyAll(e.target.checked);
                      if (e.target.checked) setNotifySuppress(false);
                    }}
                    disabled={notifySuppress}
                    size="small"
                  />
                }
                label="Notify all users"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={notifySuppress}
                    onChange={(e) => {
                      setNotifySuppress(e.target.checked);
                      if (e.target.checked) {
                        setNotifyAll(false);
                        setNotifyUsers([]);
                      }
                    }}
                    disabled={notifyAll}
                    size="small"
                  />
                }
                label="Do not send notification"
              />
              {!notifyAll && !notifySuppress && (
                <Autocomplete
                  multiple
                  options={users}
                  getOptionLabel={(option) => option.name || option.username || ''}
                  value={users.filter(user => notifyUsers.includes(user.id))}
                  onChange={(_e, value) => setNotifyUsers(value.map(user => user.id))}
                  renderInput={(params) => <TextField {...params} label="Notify Users" placeholder="Select users" size="small" />}
                  sx={{ mt: 1 }}
                />
              )}
            </Box>
          </Collapse>

          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setCommentBody('');
                setCommentType('internal');
                setNotificationType('case_comment');
                setNotifyUsers([]);
                setNotifyAll(false);
                setNotifySuppress(false);
                setSelectedProject(null);
                setProjectExplicitlyChanged(false);
                setSelectedAssignedUser(null);
                setSelectedStatus(null);
                setSelectedPriority(null);
                setSelectedType(null);
              }}
            >
              Reset
            </Button>
            <Button
              variant="contained"
              size="small"
              disabled={submittingComment || !commentBody.trim()}
              onClick={async () => {
                try {
                  if (!data) return;
                  setSubmittingComment(true);

                  const payload: any = {
                    comment_body: commentBody,
                    notify_all: notifyAll,
                    notify_suppress: notifySuppress,
                    notify_user_ids: notifyUsers,
                  };

                  const hasTaskUpdate = !!((projectExplicitlyChanged && selectedProject) || selectedAssignedUser || selectedStatus || selectedPriority || selectedType);

                  if (!hasClientRole()) {
                    if (projectExplicitlyChanged && selectedProject) payload.project_id = selectedProject.id;
                    if (selectedAssignedUser) payload.assigned_to = selectedAssignedUser.id;
                    if (selectedStatus) payload.status = selectedStatus.value;
                    if (selectedPriority) payload.priority = selectedPriority.value;
                    if (selectedType) payload.type = selectedType.value;
                    if (commentType) payload.comment_type = commentType;
                    if (notificationType) payload.notification_type = notificationType;
                  }

                  await caseService.updateCaseAndAddComment(data.id, payload);

                  const caseRes = await caseService.getCaseById(data.id);
                  setData(caseRes.data);

                  const commentsRes = await caseService.getComments(data.id);
                  setComments(Array.isArray(commentsRes.data) ? commentsRes.data : []);
                  setVisibleCommentsCount((c) => Math.max(c, 8));

                  setCommentBody('');
                  setCommentType('internal');
                  setNotificationType('case_comment');
                  setNotifyUsers([]);
                  setNotifyAll(false);
                  setNotifySuppress(false);
                  setSelectedProject(null);
                  setProjectExplicitlyChanged(false);
                  setSelectedAssignedUser(null);
                  setSelectedStatus(null);
                  setSelectedPriority(null);
                  setSelectedType(null);
                  setShowTaskFields(false);

                  showToast(
                    hasTaskUpdate ? 'Task updated and comment added successfully' : 'Comment added successfully',
                    'success'
                  );
                } catch (error: any) {
                  showToast(error.message || 'Failed to add comment', 'error');
                } finally {
                  setSubmittingComment(false);
                }
              }}
            >
              {(!hasClientRole() && ((projectExplicitlyChanged && selectedProject) || selectedAssignedUser || selectedStatus || selectedPriority || selectedType))
                ? 'Update Task & Add Comment'
                : 'Add Comment'}
            </Button>
          </Box>
          </Box>
        </Paper>
      </Box>

      {/* Timer Info - below the 50/50 layout */}
      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="h6" gutterBottom>Timer Info</Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>Time logged: {totalHms}</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
          {!isRunning && !justStopped && (
            <Button variant="contained" onClick={handleStartTimer}>Start timer</Button>
          )}
          {isRunning && (
            <>
              <Button variant="outlined" onClick={handleStopTimer}>Stop timer</Button>
            </>
          )}
          {!isRunning && justStopped && (
            <>
              <Button variant="contained" onClick={handleResumeTimer}>Resume</Button>
              <Button variant="text" onClick={handleResetTimer}>Reset</Button>
            </>
          )}
        </Box>
        {isRunning && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="primary">Active timer: {liveRunHms} (since {formatTime(new Date(runningStartedAtMs!))})</Typography>
          </Box>
        )}

        {/* Active Timers Display - Inside Timer Info */}
        {activeTimers.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                🕐 Active Timers ({activeTimers.length})
              </Typography>
              <Button 
                size="small"
                variant="text" 
                onClick={refreshActiveTimers}
                sx={{ 
                  color: 'primary.main',
                  fontSize: '0.75rem',
                  minWidth: 'auto',
                  p: 0.5
                }}
              >
                🔄
              </Button>
            </Box>
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', 
              gap: 1.5 
            }}>
              {activeTimers.map((timer) => {
                const duration = Math.floor((Date.now() / 1000) - timer.started_at);
                const hours = Math.floor(duration / 3600);
                const minutes = Math.floor((duration % 3600) / 60);
                const seconds = duration % 60;
                const timeString = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
                return (
                  <Box key={timer.session_id} sx={{
                    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                    borderRadius: 1,
                    p: 1.5,
                    border: '1px solid #e0e0e0',
                    textAlign: 'center',
                    position: 'relative',
                    overflow: 'hidden',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}>
                    <Box sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '2px',
                      background: 'linear-gradient(90deg, #ff6b6b, #4ecdc4, #45b7d1)',
                      animation: 'pulse 2s infinite'
                    }} />
                    <Typography variant="caption" sx={{ 
                      fontWeight: 'bold',
                      color: 'text.secondary',
                      fontSize: '0.7rem',
                      display: 'block',
                      mb: 0.5
                    }}>
                      {timer.user?.name || timer.user?.username || 'Unknown User'}
                    </Typography>
                    <Typography variant="h6" sx={{ 
                      fontFamily: 'monospace',
                      fontWeight: 'bold',
                      color: 'primary.main',
                      mb: 0.5,
                      letterSpacing: '0.05em',
                      fontSize: '1.1rem'
                    }}>
                      {timeString}
                    </Typography>
                    <Typography variant="caption" sx={{ 
                      color: 'text.secondary',
                      fontSize: '0.65rem'
                    }}>
                      Started {formatUnixTime(timer.started_at)}
                    </Typography>
                    <Box sx={{
                      position: 'absolute',
                      top: '6px',
                      right: '6px',
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      background: '#ff6b6b',
                      animation: 'blink 1s infinite'
                    }} />
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>Time Entries</AccordionSummary>
          <AccordionDetails>
            {/* Listing table with activity/disposition/ops */}
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Activity</TableCell>
                  <TableCell>Disposition</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell>Deductions</TableCell>
                  <TableCell>Total</TableCell>
                  <TableCell>Time</TableCell>
                  <TableCell>Note</TableCell>
                  <TableCell>Ops</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map((e) => {
                  const durTotal = e.duration_seconds || 0;
                  const durNet = Math.max(0, durTotal - (e.deduction_seconds || 0));
                  const fmt = (sec: number) => {
                    const h = Math.floor(sec / 3600);
                    const m = Math.floor((sec % 3600) / 60);
                    const s = sec % 60;
                    const parts = [] as string[];
                    if (h) parts.push(`${h}h`);
                    if (m) parts.push(`${m}m`);
                    parts.push(`${s}s`);
                    return parts.join(' ');
                  };
                  const activityName = (meta.activities || []).find((a: any) => String(a.id) === String(e.activity))?.name || e.activity || '—';
                  const dispositionName = e.disposition_label || e.disposition || '—';
                  const timeRange = `${e.started_at ? formatUnixTime(e.started_at) + ' - ' + formatUnixTime(e.stopped_at || e.started_at) : '—'} (${e.started_at ? formatUnixTimestamp(e.started_at, { month: '2-digit', day: '2-digit', year: 'numeric' }) : ''})`;
                  return (
                    <TableRow key={e.id}>
                      <TableCell>{e.user?.name || e.user?.username || '—'}</TableCell>
                      <TableCell>{activityName}</TableCell>
                      <TableCell>{dispositionName}</TableCell>
                      <TableCell>{fmt(durNet)}</TableCell>
                      <TableCell>{e.deduction_seconds ? fmt(e.deduction_seconds) : '—'}</TableCell>
                      <TableCell>{fmt(durTotal)}</TableCell>
                      <TableCell>{timeRange}</TableCell>
                      <TableCell>{e.notes || '—'}</TableCell>
                      <TableCell>
                        {isSuperAdmin && (
                          <Button size="small" onClick={() => {
                            setEditingEntryId(e.id);
                            setEditingIsSession(false);
                            setSelectedUserId(e.user?.id || null);
                            // Always reset ALL four date/time fields in NY timezone
                            // (matches the table). Unconditional sets prevent stale
                            // state from a prior interaction being silently submitted.
                            const sNY = e.started_at ? unixToNYInputs(e.started_at) : null;
                            const eNY = e.stopped_at  ? unixToNYInputs(e.stopped_at)  : null;
                            setStartDate(sNY ? sNY.date : '');
                            setStartTime(sNY ? sNY.time : '');
                            setEndDate(eNY ? eNY.date : '');
                            setEndTime(eNY ? eNY.time : '');
                            setActivity(String(e.activity || ''));
                            setDisposition(String(e.disposition || ''));
                            setEditingDispositionLabel(String(e.disposition_label || e.disposition || ''));
                            setRefId(String(e.ref_id || ''));
                            setNote(String(e.notes || ''));
                            setDeductionText(e.deduction_seconds ? String(e.deduction_seconds) : '');
                            setSelectedActivityIds(Array.isArray(e.activity_ids) ? e.activity_ids : []);
                            setJustStopped(true);
                          }}>edit</Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {entries.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">No time entries.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </AccordionDetails>
        </Accordion>


        {editingEntryId && (
          <Paper sx={{ p: 3, mb: 2 }}>
            <Typography variant="h6" gutterBottom>Time Entry</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
              {isSuperAdmin && (
                <Autocomplete
                  options={users}
                  getOptionLabel={(o) => o.name || o.username}
                  value={users.find((u) => u.id === selectedUserId) || null}
                  onChange={(_e, v) => setSelectedUserId(v ? v.id : null)}
                  renderInput={(params) => <TextField {...params} label="User" />}
                  sx={{ minWidth: 220 }}
                />
              )}
              <Autocomplete
                options={meta.activities || []}
                getOptionLabel={(o) => o.name}
                value={(meta.activities || []).find((a: any) => String(a.id) === String(activity)) || null}
                onChange={(_e, v) => setActivity(v ? String(v.id) : '')}
                renderInput={(params) => <TextField {...params} label="Activity *" />}
                sx={{ minWidth: 220 }}
              />
              {/* Billable Activities Multi-select - shown only for time-based projects */}
              {isTimeBasedProject(projectSettings) && projectActivities.length > 0 && (
                <Autocomplete
                  multiple
                  options={projectActivities}
                  getOptionLabel={(o) => o.name}
                  value={projectActivities.filter((a: any) => selectedActivityIds.includes(a.id))}
                  onChange={(_e, v) => setSelectedActivityIds(v.map((a: any) => a.id))}
                  renderInput={(params) => <TextField {...params} label="Billable Activities" placeholder="Select activities" />}
                  renderTags={(value, getTagProps) =>
                    value.map((option: any, index: number) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.id}
                        label={option.name}
                        size="small"
                      />
                    ))
                  }
                  sx={{ minWidth: 300 }}
                />
              )}
              <TextField label="Ref ID" value={refId} onChange={(e) => setRefId(e.target.value)} sx={{ minWidth: 220 }} />
              <Autocomplete
                options={dispositionDropdownOptions}
                getOptionLabel={(o) => o.name}
                value={dispositionDropdownOptions.find((d: any) => String(d.id) === String(disposition)) || null}
                onChange={(_e, v) => {
                  setDisposition(v ? String(v.id) : '');
                  setEditingDispositionLabel(v ? String(v.name).replace(/ \(inactive\)$/, '') : '');
                }}
                renderInput={(params) => <TextField {...params} label="Dispositions *" />}
                sx={{ minWidth: 220 }}
              />
              <TextField label="Start Date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} sx={{ minWidth: 180 }} disabled={!isSuperAdmin} />
              <TextField label="Start Time" type="time" inputProps={{ step: 1 }} value={startTime} onChange={(e) => setStartTime(e.target.value)} sx={{ minWidth: 160 }} disabled={!isSuperAdmin} />
              <TextField label="End Date" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} sx={{ minWidth: 180 }} disabled={!isSuperAdmin} />
              <TextField label="End Time" type="time" inputProps={{ step: 1 }} value={endTime} onChange={(e) => setEndTime(e.target.value)} sx={{ minWidth: 160 }} disabled={!isSuperAdmin} />
              <TextField label="Deductions (seconds)" placeholder="e.g., 120" value={deductionText} onChange={(e) => setDeductionText(e.target.value)} sx={{ minWidth: 200 }} disabled={!isSuperAdmin} />
              <TextField label="Note" value={note} onChange={(e) => setNote(e.target.value)} multiline minRows={2} sx={{ flex: '1 1 300px' }} />
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="contained" disabled={!editingEntryId || !activity || !disposition} onClick={async () => {
                if (!data || !editingEntryId) return;
                // Convert NY-timezone form inputs to UTC Unix seconds
                const startUnix = nyInputsToUnix(startDate, startTime);
                const stopUnix  = nyInputsToUnix(endDate, endTime);
                if (startUnix !== undefined && stopUnix !== undefined && startUnix >= stopUnix) {
                  showToast('Start time must be before end time', 'error');
                  return;
                }
                try {
                  if (editingIsSession) {
                    const payload: any = {
                      session_id: editingEntryId,
                      notes: note || undefined,
                      activity: activity || undefined,
                      ref_id: refId || undefined,
                      disposition: disposition || undefined,
                      started_at: startUnix,
                      stopped_at: stopUnix,
                    };
                    if (isSuperAdmin && deductionText) {
                      const sec = parseInt(deductionText.trim(), 10);
                      payload.deduction_seconds = isNaN(sec) || sec < 0 ? 0 : sec;
                    }
                    // Add activity_ids if project is time-based and activities are selected
                    if (isTimeBasedProject(projectSettings) && selectedActivityIds.length > 0) {
                      payload.activity_ids = selectedActivityIds;
                    }
                    await caseService.finalizeTimer(data.id, payload);
                  } else {
                    // update existing entry
                    const payload: any = {
                      notes: note || undefined,
                      activity: activity || undefined,
                      ref_id: refId || undefined,
                      disposition: disposition || undefined,
                      started_at: startUnix,
                      stopped_at: stopUnix,
                      activity_ids: selectedActivityIds,
                    };
                    if (isSuperAdmin && deductionText) {
                      const sec = parseInt(deductionText.trim(), 10);
                      payload.deduction_seconds = isNaN(sec) || sec < 0 ? 0 : sec;
                    }
                    await caseService.updateTimeEntry(data.id, editingEntryId, payload);
                  }
                  const timer = await caseService.listTimeEntries(data.id);
                  setEntries(timer.data.entries || []);
                  setTotalSeconds(timer.data.total_seconds || 0);
                  setJustStopped(false);
                  setEditingEntryId(null);
                  setEditingIsSession(false);
                  setEditingDispositionLabel('');
                  setSelectedActivityIds([]);
                } catch (e: any) {
                  showToast(e.message || 'Failed to save time entry', 'error');
                }
              }}>Save</Button>
              {!editingIsSession && (
                <Button variant="outlined" color="error" disabled={!editingEntryId || !isSuperAdmin} onClick={async () => {
                  if (!data || !editingEntryId) return;
                  try {
                    await caseService.deleteTimeEntry(data.id, editingEntryId);
                    const timer = await caseService.listTimeEntries(data.id);
                    setEntries(timer.data.entries || []);
                    setTotalSeconds(timer.data.total_seconds || 0);
                    setJustStopped(false);
                    setEditingEntryId(null);
                    setEditingDispositionLabel('');
                  } catch (e: any) {
                    showToast(e.message || 'Failed to delete time entry', 'error');
                  }
                }}>Delete</Button>
              )}
            </Box>
          </Paper>
        )}
      </Paper>

      {/* Checklist Panel - only shown when project has checklist enabled */}
      {projectSettings?.custom_fields?.enable_checklist && (
      <Accordion 
        expanded={expandedAccordions.checklist}
        onChange={() => setExpandedAccordions(prev => ({ ...prev, checklist: !prev.checklist }))}
        sx={{ mb: 2 }}
      >
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon />}
          sx={{
            backgroundColor: themeColors.primary,
            '&:hover': { backgroundColor: themeColors.dark },
            color: 'white',
          }}
        >
          <Typography variant="h6" sx={{ color: 'white' }}>Checklist Details</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {projectSettings?.custom_fields?.checklist_instructions ? (
            <Box
              sx={{ '& ul': { pl: 3 }, '& ol': { pl: 3 } }}
              dangerouslySetInnerHTML={{ __html: projectSettings.custom_fields.checklist_instructions }}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">No checklist instructions configured for this project.</Typography>
          )}
        </AccordionDetails>
      </Accordion>
      )}

      {/* Case Records (File Manager) */}
      <Accordion 
        expanded={expandedAccordions.caseRecords}
        onChange={() => setExpandedAccordions(prev => ({ ...prev, caseRecords: !prev.caseRecords }))}
        sx={{ mb: 2 }}
      >
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon />}
          sx={{
            backgroundColor: themeColors.primary,
            '&:hover': { backgroundColor: themeColors.dark },
            color: 'white',
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ color: 'white' }}>
              {hasClientRole() ? 'Your Case Records' : 'Case Files'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', display: 'block' }}>
              {hasClientRole()
                ? 'View and download files for this case'
                : 'Upload, browse, and manage case files by folder'}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <CaseFilesPanel
            caseId={data.id}
            caseStatus={data.status}
            readOnly={hasClientRole()}
            showDocumentReview={isPmpAdmin()}
            showSync={isPmpAdminOrSiteAdmin()}
            hasVendorAssignment={!!((data as any).vendor_assignment)}
            hasClientAssignment={!!((data as any).client_assignment)}
            navigateToReview={(fileId) =>
              navigate(
                firmId
                  ? `/firms/${firmId}/cases/${caseId}/document-review/${fileId}`
                  : `/cases/${caseId}/document-review/${fileId}`
              )
            }
            onToast={showToast}
            onCaseReopened={async () => {
              try {
                const caseRes = await caseService.getCaseById(data.id);
                setData(caseRes.data);
              } catch (_e) {
                // ignore refresh errors
              }
            }}
          />
        </AccordionDetails>
      </Accordion>


      {/* Vendor Estimation Section - Show ONLY for Vendor firms (vendor users or PMP admins viewing vendor firm cases) */}
      {data && firm && firm.type === 'vendor' && (isVendorUser() || isSuperAdmin) && (
        <Accordion 
          expanded={expandedAccordions.caseEstimation}
          onChange={() => setExpandedAccordions(prev => ({ ...prev, caseEstimation: !prev.caseEstimation }))}
          sx={{ mb: 2 }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: themeColors.primary,
              '&:hover': { backgroundColor: themeColors.dark },
              color: 'white',
            }}
          >
            <Typography variant="h6" sx={{ color: 'white' }}>Vendor Billing Estimate</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <CaseEstimation
              caseId={data.id}
              projectId={data.project_id}
              user={user}
              isVendorStaff={isVendorUser()}
            />
          </AccordionDetails>
        </Accordion>
      )}

      {/* Client Estimation Section - Show ONLY for Client firms (client firm users or PMP admins viewing client firm cases) */}
      {data && firm && firm.type === 'client' && (isClientFirmUser() || isSuperAdmin) && (
        <Accordion 
          expanded={expandedAccordions.clientEstimation}
          onChange={() => setExpandedAccordions(prev => ({ ...prev, clientEstimation: !prev.clientEstimation }))}
          sx={{ mb: 2 }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: themeColors.primary,
              '&:hover': { backgroundColor: themeColors.dark },
              color: 'white',
            }}
          >
            <Typography variant="h6" sx={{ color: 'white' }}>Client Billing Estimate</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <ClientEstimation
              caseId={data.id}
              projectId={data.project_id}
              user={user}
            />
          </AccordionDetails>
        </Accordion>
      )}

      {/* Toast Notifications */}
      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseToast} 
          severity={toast.severity} 
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>

      {/* Vendor Assignment/Reassignment Dialogs */}
      {/* Initial Assignment Dialog - Reuse the reassignment dialog but without current vendor */}
      <VendorReassignDialog
        open={assignDialogOpen}
        onClose={() => setAssignDialogOpen(false)}
        onReassign={handleAssignVendor}
        currentVendor={undefined}
        currentVendorUser={undefined}
      />

      {/* Vendor Reassignment Dialog */}
      <VendorReassignDialog
        open={reassignDialogOpen}
        onClose={() => setReassignDialogOpen(false)}
        onReassign={handleReassignVendor}
        currentVendor={(data as any)?.assigned_vendor || (data as any)?.assignedVendor}
        currentVendorUser={(data as any)?.assigned_vendor_user || (data as any)?.assignedVendorUser}
      />

      <VendorAssignmentHistory
        open={historyDialogOpen}
        onClose={() => setHistoryDialogOpen(false)}
        history={assignmentHistory}
      />

      {/* Delete Case Confirmation Dialog */}
      <Dialog open={deleteCaseDialogOpen} onClose={() => !deletingCase && setDeleteCaseDialogOpen(false)}>
        <DialogTitle>Delete Case</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete case "{data?.title}"? The case will be hidden from the system and can only be restored by a database administrator.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteCaseDialogOpen(false)} disabled={deletingCase}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteCase}
            disabled={deletingCase}
          >
            {deletingCase ? 'Deleting...' : 'Delete Case'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default CaseView;



